/**
 * Split a free-form transcript string into roughly sentence-sized chunks
 * for the line-by-line "transcript shrinks as note grows" reveal in
 * `MicTestPreview`. The original Learn-the-Basics demo (Jacob Tait,
 * #9177) used a hand-curated `TranscriptLine[]` array with paired
 * `raw` / `enhanced` entries; once we wired the preview to per-specialty
 * `/onboarding-v2-assets` content the BE only ships a single transcript
 * string per specialty, so we re-derive the chunks here.
 *
 * The split is intentionally simple — sentence boundaries on `.`, `!`,
 * `?` followed by whitespace, plus newlines — because the seeded
 * specialty transcripts are short (1–4 sentences) and the visual
 * pairing with note sections is approximate, not semantic.
 *
 * Empty input returns an empty array so the caller can short-circuit
 * the "no transcript to consume" path.
 */
export function splitTranscriptIntoChunks(transcript: string): string[] {
  const trimmed = transcript.trim();
  if (!trimmed) return [];

  // Sentence boundary: `.`/`!`/`?` followed by whitespace OR a newline.
  // Lookbehind keeps the punctuation attached to the preceding chunk so
  // the rendered text reads "…three days ago." rather than "…three days
  // ago" + a stranded period on the next chunk.
  const rawSegments = trimmed
    .split(/(?<=[.!?])\s+|\n+/u)
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);

  // Several seeded transcripts open with patient titles ("Mrs. Lee, …",
  // "Mr. Khan, …", "Dr. Patel, …") which the naive regex would split
  // into a stranded "Mrs." chunk. Glue any segment that ends in a known
  // honorific or abbreviation back onto the next segment so the visible
  // chunks stay clause-shaped.
  const merged: string[] = [];
  for (const segment of rawSegments) {
    const previous = merged[merged.length - 1];
    if (previous !== undefined && endsWithAbbreviation(previous)) {
      merged[merged.length - 1] = `${previous} ${segment}`;
    } else {
      merged.push(segment);
    }
  }

  // If the transcript is one long unpunctuated string the regex above
  // returns the entire input as a single segment — that's the right
  // behaviour: we'd rather show one chunk than break mid-clause.
  return merged;
}

/** Honorifics + Latin abbreviations that should never end a chunk. */
const ABBREVIATIONS: readonly string[] = [
  'Mr',
  'Mrs',
  'Ms',
  'Mx',
  'Dr',
  'Prof',
  'Sr',
  'Jr',
  'St',
  'Sgt',
  'Lt',
  'Capt',
  'e.g',
  'i.e',
  'vs',
  'etc',
  'No',
  'Approx',
];

const ABBREVIATION_TAIL = new RegExp(`(?:^|\\s)(?:${ABBREVIATIONS.join('|')})\\.$`, 'i');

function endsWithAbbreviation(segment: string): boolean {
  return ABBREVIATION_TAIL.test(segment);
}
